import {
  Geogroup,
  GeogroupNewsPost,
  GeogroupService,
  NewsPostCard,
  UserGeogroup,
  useCancellablePromise,
} from '@geovelo-frontends/commons';
import { Add } from '@mui/icons-material';
import { Box, Divider, Pagination, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import emptyStateImage from '../../assets/images/news-illus.svg';
import Button from '../../components/button';
import { DialogWithContent } from '../../components/dialog';
import { AppContext } from '../../context';

import NewsFormDialog from './news-form-dialog';

const pageSize = 10;

function News({
  geogroup,
  userGeogroup,
}: {
  geogroup: Geogroup | undefined;
  userGeogroup?: UserGeogroup | null;
}): React.JSX.Element {
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState<number>();
  const [newsPosts, setNewsPosts] = useState<GeogroupNewsPost[]>();
  const [newsPostToUpdate, setNewsPostToUpdate] = useState<GeogroupNewsPost | null>(null);
  const [newsPostFormDialogOpen, openNewsPostFormDialog] = useState(false);
  const {
    user: { current: currentUser },
  } = useContext(AppContext);
  const { t } = useTranslation();
  const { transitions } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { cancellablePromise, cancelPromises } = useCancellablePromise();

  useEffect(() => {
    if (geogroup) getGeogroupNews(geogroup.id);

    return () => {
      cancelPromises();
      setNewsPosts(undefined);
    };
  }, [geogroup, page]);

  async function getGeogroupNews(geogroupId: number) {
    try {
      cancelPromises();
      setNewsPosts(undefined);

      const { count, results } = await cancellablePromise(
        GeogroupService.getNewsPosts(geogroupId, { ordering: '-created', page, pageSize }),
      );

      setPagesCount(Math.ceil(count / pageSize));
      setNewsPosts(results);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('commons.news.server_error'), { variant: 'error' });
      }
    }
  }

  function handleNewsPostFormDialogOpen(post?: GeogroupNewsPost) {
    setNewsPostToUpdate(post || null);
    openNewsPostFormDialog(true);
  }

  function handleNewsPostFormDialogClose(post?: GeogroupNewsPost) {
    if (post && newsPosts) {
      if (newsPostToUpdate) {
        const index = newsPosts.findIndex(({ id }) => id === newsPostToUpdate.id);
        if (index >= 0) {
          const updatedNewsPosts = [...newsPosts];
          updatedNewsPosts.splice(index, 1, post);
          setNewsPosts(updatedNewsPosts);
        }
      } else {
        const updatedNewsPosts = [post, ...newsPosts];
        setNewsPosts(updatedNewsPosts);
      }
    }

    openNewsPostFormDialog(false);
    setTimeout(() => setNewsPostToUpdate(null), transitions.duration.leavingScreen);
  }

  return (
    <>
      <Box display="flex" flexDirection="column" gap={4}>
        <Box alignItems="center" display="flex" gap={3} justifyContent="space-between">
          <Typography component="h2" fontWeight={700} variant="h6">
            {t('geovelo.communities.news.title')}
          </Typography>
          {geogroup && geogroup.type !== 'city' && (!newsPosts || newsPosts.length > 0) && (
            <Button
              color="primary"
              disabled={!newsPosts}
              onClick={() => handleNewsPostFormDialogOpen()}
              size="small"
              startIcon={<Add />}
              variant="outlined"
            >
              {t('geovelo.communities.actions.create_post')}
            </Button>
          )}
        </Box>
        {!newsPosts || newsPosts.length > 0 ? (
          <>
            <Box display="flex" flexDirection="column" gap={3}>
              {newsPosts
                ? newsPosts.map((newsPost, index) => (
                    <Fragment key={newsPost.id}>
                      {index > 0 && <Divider />}
                      <NewsPostCard
                        currentPartner={geogroup?.partner}
                        currentUser={currentUser}
                        Dialog={DialogWithContent}
                        geogroup={geogroup}
                        newsPost={newsPost}
                        onEdit={() => handleNewsPostFormDialogOpen(newsPost)}
                        onRemoved={() => {
                          if (page > 1) setPage(1);
                          else if (geogroup) getGeogroupNews(geogroup.id);
                        }}
                        userType={userGeogroup?.isAdmin ? 'admin' : 'user'}
                      />
                    </Fragment>
                  ))
                : [0, 1, 2].map((key, index) => (
                    <Fragment key={key}>
                      {index > 0 && <Divider />}
                      <NewsPostCard />
                    </Fragment>
                  ))}
            </Box>
            <Box alignSelf="center">
              <Pagination count={pagesCount} onChange={(_, page) => setPage(page)} page={page} />
            </Box>
          </>
        ) : (
          <Box
            alignItems="center"
            display="flex"
            flexDirection="column"
            gap={5}
            paddingTop={8}
            width="100%"
          >
            <img height={100} src={emptyStateImage} />
            <Box display="flex" flexDirection="column">
              <Typography align="center">{t('geovelo.communities.news.empty_state')}</Typography>
            </Box>
            {geogroup && geogroup.type !== 'city' && (
              <Button
                color="primary"
                onClick={() => handleNewsPostFormDialogOpen()}
                size="small"
                startIcon={<Add />}
                variant="contained"
              >
                {t('geovelo.communities.actions.create_post')}
              </Button>
            )}
          </Box>
        )}
      </Box>
      <NewsFormDialog
        geogroup={geogroup}
        onClose={handleNewsPostFormDialogClose}
        open={newsPostFormDialogOpen}
        post={newsPostToUpdate}
        userGeogroup={userGeogroup}
      />
    </>
  );
}

export default News;
